// components/MyCv.tsx
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaDownload, FaTimes } from 'react-icons/fa';

interface MyCvProps {
  isOpen: boolean;
  onClose: () => void;
}

const MyCv: React.FC<MyCvProps> = ({ isOpen, onClose }) => {
  // CV PDF file
  const cvPath = "./myCv.pdf"; 
  
  const handleDownload = () => {
    // Create anchor element
    const link = document.createElement('a');
    link.href = cvPath;
    link.download = 'EVODENUBY_CV.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Prevent scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          >
            {/* Modal Container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-[#1a1f2e] rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-[#0a0f1a] dark:to-[#1a1f2e]">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                    My Curriculum Vitae
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    EVODENUBY • Full Stack Developer | Aspiring Data scientist | Graphic Designer
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Download Button */}
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95"
                  >
                    {React.createElement(FaDownload as React.ComponentType<{ className?: string }>, { className: "text-xl" })}
                    
                    Download CV
                  </button>
                  
                  {/* Close Button */}
                  <button
                    onClick={onClose}
                    className="p-3 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    aria-label="Close"
                  >
                 {React.createElement(FaTimes as React.ComponentType<{ className?: string }>, { className: "text-xl" })}
                    
                  </button>
                </div>
              </div>

              {/* CV Preview */}
              <div className="flex-1 overflow-hidden p-2">
                {/*  PDF Viewer (if PDF is available) */}
                <div className="w-full h-full rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-900">
                  <iframe
                    src={cvPath}
                    className="w-full h-full min-h-[70vh]"
                    title="EVODENUBY CV Preview"
                    style={{ border: 'none' }}
                  />
                </div>

                {/*Image Preview (if using image CV) */}
                {/* 
                <div className="w-full h-full overflow-auto">
                  <img 
                    src="/evodenuby-cv.jpg" 
                    alt="EVODENUBY CV" 
                    className="w-full h-auto max-w-4xl mx-auto"
                  />
                </div>
                */}

                {/* Option 3: HTML CV Content (if no PDF) */}
                {/*
                <div className="max-w-4xl mx-auto p-8 overflow-y-auto max-h-[70vh]">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg">
                    <div className="text-center mb-8">
                      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">EVODENUBY</h1>
                      <p className="text-xl text-blue-600 dark:text-blue-400 mb-4">Full Stack Developer</p>
                      <div className="flex flex-wrap justify-center gap-4 text-gray-600 dark:text-gray-400">
                        <span>📧 your.email@example.com</span>
                        <span>📱 +123 456 7890</span>
                        <span>📍 Your Location</span>
                      </div>
                    </div>
                    
                    <div className="space-y-8">
                      <section>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 border-b pb-2">Experience</h2>
                        <div className="space-y-4">
                          <div className="border-l-4 border-blue-500 pl-4">
                            <h3 className="text-xl font-semibold">Senior Developer</h3>
                            <p className="text-gray-600 dark:text-gray-400">Company Name • 2022-Present</p>
                            <p className="mt-2">Description of your role and achievements...</p>
                          </div>
                        </div>
                      </section>
                      
                      <section>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4 border-b pb-2">Skills</h2>
                        <div className="flex flex-wrap gap-2">
                          {['React', 'TypeScript', 'Node.js', 'Python', 'AWS', 'MongoDB'].map((skill) => (
                            <span key={skill} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
                */}
              </div>

              {/* Footer */}
              <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-[#0a0f1a] text-center text-sm text-gray-600 dark:text-gray-400">
                <p>© {new Date().getFullYear()} EVODENUBY. All rights reserved.</p>
                <p className="mt-1">Last updated: {new Date().toLocaleDateString()}</p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MyCv;